import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Col, Layout, Row, Input, Select, DatePicker } from 'antd';
import { capitalize, get, omit } from 'lodash';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
import queryString from 'query-string';

import ResellerServices from 'services/reseller';

import { TableList } from 'components/table-list';
import PdButton from 'components/pd-button';

import { ProfileContext } from 'contexts/profile';

import { errHandling } from 'utils';

const { Content } = Layout;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Search } = Input;

export function ActiveResellers() {
  const location = useLocation();
  const { fetchProfile } = useContext(ProfileContext);

  const [activeList, setActiveList] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [endDatePick, setEndDate] = useState('');
  const [loadStatus, setLoadStatus] = useState(true);
  const [nextPage, setNextPage] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [startDatePick, setStartDate] = useState('');
  const [type, setType] = useState('');

  const navigate = useNavigate();
  const parsed = queryString.parse(location.search);
  const dateFormat = 'YYYY/MM/DD';

  function customElement(row) {
    return (
      <div className="button-table">
        <Link to={`/resellers_active/detail?id=${row.id}`}>
          <PdButton type="primary" style={{ height: 32, width: 100 }}>
            Detail
          </PdButton>
        </Link>
      </div>
    );
  }

  customElement.displayName = 'customButton';

  const [columns] = useState([
    {
      name: 'Name',
      selector: row => row.first_name,
      format: (row) =>
        `${get(row, 'first_name', '')} ${get(row, 'last_name', '')}`,
      width: '20%',
    },
    {
      name: 'Store Name',
      selector: row => row.store_name,
      width: '20%',
    },
    {
      name: 'Type',
      selector: row => row.reseller_type,
      format: (row) => {
        if (row.reseller_type) return capitalize(row.reseller_type);
        else return '';
      },
      width: '20%',
      center: true,
    },
    {
      name: 'Joined Date',
      selector: row => row.regitered_at,
      format: (row) => {
        return dayjs(row.registered_at).format('DD/MM/YYYY');
      },
      width: '20%',
      center: true,
    },
    {
      name: '',
      cell: customElement,
      center: true,
      width: '20%',
    },
  ]);

  async function fetchResellers({
    endDate,
    page,
    q,
    reseller_type,
    startDate,
  }) {
    setLoadStatus(true);
    try {
      let end = endDate;
      if (endDate) {
        end = dayjs(endDate, dateFormat).add(1, 'days');
        end = dayjs(end).format(dateFormat);
      }
      const { data } = await ResellerServices.getResellerList({
        endDate: end,
        page,
        q,
        reseller_type,
        startDate,
        status: 'accepted',
      });
      const resellerData = get(data, 'data', []);
      const cursor = get(data, 'metadata.next_cursor', -1);
      setActiveList(resellerData);
      setNextPage(cursor);
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadStatus(false);
    }
  }

  function changePage(page) {
    if (page < 1) page = 1;
    parsed['page'] = page;
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/resellers_active',
      search: stringified,
    });
  }

  function onChangeDate(dates, [startDate, endDate]) {
    if (startDate === '') delete parsed['start_date'];
    else parsed['start_date'] = startDate;

    if (endDate === '') delete parsed['end_date'];
    else parsed['end_date'] = endDate;

    parsed['page'] = 1;

    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/resellers_active',
      search: stringified,
    });
  }

  function filterReseller(param, val) {
    if (!val) delete parsed[param];
    else parsed[param] = val;

    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/resellers_active',
      search: stringified,
    });
  }

  function resetFilter() {
    const stringified = queryString.stringify(
      omit(parsed, [
        'end_date',
        'limit',
        'page',
        'q',
        'reseller_type',
        'start_date',
      ])
    );
    navigate({
      pathname: '/resellers_active',
      search: stringified,
    });
  }

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* eslint-disable */
  useEffect(() => {
    if (parsed['q']) setSearchValue(parsed['q']);
    else setSearchValue('');

    if (parsed['page']) {
      if (parsed['page'] < 1) {
        parsed['page'] = 1;
        const stringified = queryString.stringify(parsed);
        navigate({
          pathname: '/resellers_status',
          search: stringified,
        });
      } else setCurrentPage(parsed['page']);
    } else setCurrentPage(1);

    if (parsed['reseller_type']) setType(parsed['reseller_type']);
    else setType('');

    if (parsed['start_date']) setStartDate(parsed['start_date']);
    else setStartDate('');

    if (parsed['end_date']) setEndDate(parsed['end_date']);
    else setEndDate('');

    fetchResellers({
      endDate: parsed['end_date'],
      limit: 10,
      page: parsed['page'],
      q: parsed['q'],
      reseller_type: parsed['reseller_type'],
      startDate: parsed['start_date'],
    });
  }, [
    parsed['end_date'],
    parsed['page'],
    parsed['q'],
    parsed['reseller_type'],
    parsed['start_date'],
  ]);
  /* eslint-enable */

  return (
    <Layout className="main-container">
      <div className="content">
        <div className="pageTitle">Active Resellers</div>
        <Content className="pageContent">
          <Row gutter={18}>
            <Col span={14}>
              <Search
                value={searchValue}
                onChange={(event) =>
                  setSearchValue(get(event, 'target.value', event))
                }
                enterButton
                className="search-product"
                placeholder="Search Active Reseller"
                onSearch={filterReseller.bind(this, 'q')}
              />
            </Col>
            <Col span={4} offset={5} className="height-32">
              <div className="invisible">invisible</div>
            </Col>
            <Col span={7} className="mt-1">
              <RangePicker
                className="w-full"
                format={dateFormat}
                onChange={onChangeDate}
                value={[
                  startDatePick && dayjs(startDatePick, dateFormat),
                  endDatePick && dayjs(endDatePick, dateFormat),
                ]}
              />
            </Col>
            <Col span={3} className="mt-1">
              <Select
                value={type}
                style={{ width: '100%' }}
                onChange={filterReseller.bind(this, 'reseller_type')}>
                <Option value="" selected hidden>
                  Type
                </Option>
                <Option value="dealer">Dealer</Option>
                <Option value="individual">Individual</Option>
                <Option value="buyer">Buyer</Option>
              </Select>
            </Col>
            <Col span={4} className="mt-1">
              <span className="reset-filter" onClick={resetFilter}>
                Reset Filter
              </span>
            </Col>

            {!loadStatus && (
              <TableList
                changePage={changePage}
                columns={columns}
                loadingOrder={loadStatus}
                nextPage={nextPage}
                orderList={activeList}
                page={currentPage}
                responsive={false}
              />
            )}
          </Row>
        </Content>
      </div>
    </Layout>
  );
}

export default { ActiveResellers };
